import { getAccessToken } from '@nexus/supabase';

import type { ChatsResponse, ChatResponse } from '../hooks/chats/types';

// ----------------------------------------------------------------------
// API Configuration
// ----------------------------------------------------------------------

let supabaseUrl = '';

/**
 * Configure the Supabase URL for API calls.
 * Must be called before using any chat API functions.
 */
export function configureChatsApi(url: string) {
  supabaseUrl = url;
}

// ----------------------------------------------------------------------
// Fetch Helper
// ----------------------------------------------------------------------

async function fetchWithAuth<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured. Call configureChatsApi() first.');
  }

  const token = await getAccessToken();

  const url = new URL(`${supabaseUrl}/functions/v1/root/${endpoint}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ----------------------------------------------------------------------
// API Functions
// ----------------------------------------------------------------------

/**
 * Fetch all chats for the authenticated user
 */
export async function fetchChats(limit = 50, offset = 0): Promise<ChatsResponse> {
  const response = await fetchWithAuth<ChatsResponse>('chats', {
    limit: String(limit),
    offset: String(offset),
  });

  return response;
}

/**
 * Fetch a single chat by ID
 */
export async function fetchChat(id: string): Promise<ChatResponse> {
  const response = await fetchWithAuth<ChatResponse>(`chats/${id}`);

  return response;
}

/**
 * Delete a chat by ID
 */
export async function deleteChatApi(id: string): Promise<void> {
  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured. Call configureChatsApi() first.');
  }

  const token = await getAccessToken();

  const response = await fetch(`${supabaseUrl}/functions/v1/root/chats/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

// ----------------------------------------------------------------------
// Export Types
// ----------------------------------------------------------------------

export interface ExportMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ExportChat {
  id: string;
  title: string;
  summary: string;
  timestamp: string | null;
  message_count: number;
  messages: ExportMessage[];
}

export interface ExportResponse {
  version: string;
  exported_at: string;
  user_id: string;
  chats: ExportChat[];
  total_chats: number;
  total_messages: number;
}

// ----------------------------------------------------------------------
// Export Function
// ----------------------------------------------------------------------

/**
 * Export all chats for the authenticated user as JSON
 */
export async function exportChats(): Promise<ExportResponse> {
  return fetchWithAuth<ExportResponse>('chats/export');
}
